import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LoadingSpinner } from '@tadagram/ui';
import { Dashboard, Settings, AccountManagement, CampaignManagement, WorkflowBuilder, GroupPostLeadsDashboard, EngagementPostLeadsDashboard, JoinedGroupsDashboard, SeedingContentsManagement } from './pages';
import { ScheduleDashboard } from './pages/Schedules';
import { ReportDashboard } from './pages/Reports';
import FanpageOAuthCallback from './pages/FanpageManagement/FanpageOAuthCallback';
import { authService } from './services/auth';
import { apiService } from './services/api';
import { AppHeader } from './components/layout/AppHeader';
import { PlatformSidebar } from './components/layout/PlatformSidebar';
import { WorkerListSidebar } from './components/layout/WorkerListSidebar';
import { Worker } from './components/layout/WorkerItem';
import { useSSE } from './hooks/useSSE';
import { isTrustedShellOrigin } from './constants/origins';
import { WorkflowTourProvider } from './components/tour';
import { AgentMount } from './components/agent/AgentMount';
import i18n from './i18n';
import { useIframeNavigationSync } from './hooks/useIframeNavigationSync';
import { TikTokQRModal } from './components/TikTokQRModal';
import './App.css'; // New layout styles

/** Null-rendering component — must be inside <Router> to use useLocation. */
function IframeNavSync() {
  useIframeNavigationSync();
  return null;
}

// Version: 1.2.0 - Auth via shell postMessage only (no standalone login)
function App() {
  const normalizedPathname = window.location.pathname.replace(/\/+$/, '');
  const isFanpageCallbackRoute = normalizedPathname.endsWith('/fanpage-callback');
  const urlParams = new URLSearchParams(window.location.search);
  const hasOAuthCodeAndState = urlParams.has('code') && urlParams.has('state');
  const shouldHandleFanpageCallback = isFanpageCallbackRoute || hasOAuthCodeAndState;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // 3-column layout state
  const [activePlatform, setActivePlatform] = useState('facebook');
  const [activeWorkerId, setActiveWorkerId] = useState<string | null>(null);
  
  // Real data from API
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  // TikTok QR login modal state
  const [tiktokQRAccount, setTiktokQRAccount] = useState<any | null>(null);
  const [tiktokQRInitial, setTiktokQRInitial] = useState<{ qr_base64: string; expires_at: number } | undefined>(undefined);
  
  // Prefetched worker URL + JWT for schedules — keyed by worker UUID so that
  // switching between multiple workers under the same business ID doesn't evict
  // the previously-fetched tunnel URL. Each worker's cache has a 50-min TTL.
  const [prefetchedScheduleCache, setPrefetchedScheduleCache] = useState<Map<string, {
    workerURL: string;
    jwt: string;
    timestamp: number;
    workerId: string;
  }>>(new Map());
  
  // SSE: Real-time updates from backend
  useSSE({
    enabled: isAuthenticated,
    onAccountActivated: (data) => {
      setAccounts(prev => prev.map(account => 
        account._id === data.account_id || account.id === data.account_id
          ? { ...account, status: 'active', error: null, updated_at: data.timestamp }
          : account
      ));
    },
    onAccountFailed: (data) => {
      setAccounts(prev => prev.map(account => 
        account._id === data.account_id || account.id === data.account_id
          ? { ...account, status: 'failed', error: data.error, updated_at: data.timestamp }
          : account
      ));
    },
    onWorkflowActivated: (_data) => {
      // Future: Handle workflow events if needed
    },
    onWorkflowFailed: (_data) => {
      // Future: Handle workflow events if needed
    },
    onWorkflowCompleted: (data) => {
      // One-shot workflow finished — refresh list badge and notify WorkflowBuilder if open
      window.dispatchEvent(new CustomEvent('workflow-status-changed'));
      window.dispatchEvent(new CustomEvent('workflow-one-shot-completed', { detail: { workflow_id: data?.workflow_id } }));
    },
    onTikTokQRReady: (data) => {
      // Find the matching account and show QR modal
      const matchedAccount = accounts.find(
        (a) => a._id === data.account_id || a.id === data.account_id
      );
      if (matchedAccount) {
        setTiktokQRAccount(matchedAccount);
        setTiktokQRInitial({ qr_base64: data.qr_base64, expires_at: data.expires_at });
      }
      // Also dispatch a DOM event so WorkerManagement can open the modal if it's active
      window.dispatchEvent(new CustomEvent('tiktok-qr-ready', { detail: data }));
    },
  });
  
  // Get worker UUIDs for validation
  const workerUUIDs = workers.map(w => w.uuid);
  
  // Calculate platform counts from ALL accounts (regardless of worker assignment)
  const platformCounts = {
    facebook: accounts.filter(a => a.platform === 'facebook').length,
    instagram: accounts.filter(a => a.platform === 'instagram').length,
    tiktok: accounts.filter(a => a.platform === 'tiktok').length,
    zalo: accounts.filter(a => a.platform === 'zalo').length,
    threads: accounts.filter(a => a.platform === 'threads').length,
  };
  
  // Always show all workers in sidebar regardless of which platform is selected.
  // Workers that happen to have no accounts for the active platform still need to be
  // accessible so the user can add accounts to them.
  const filteredWorkers = workers;
  
  // Auto-select first worker when filtered list changes
  useEffect(() => {
    if (filteredWorkers.length > 0 && !activeWorkerId) {
      setActiveWorkerId(filteredWorkers[0].uuid);
    } else if (filteredWorkers.length === 0) {
      // Reset activeWorkerId when no workers match the platform
      setActiveWorkerId(null);
    } else if (activeWorkerId && !filteredWorkers.some(w => w.uuid === activeWorkerId)) {
      // Reset to first worker if current selection is not in filtered list
      setActiveWorkerId(filteredWorkers[0].uuid);
    }
  }, [filteredWorkers, activeWorkerId]);
  
  // Check for orphaned accounts (accounts without workers)
  const orphanedAccounts = accounts.filter(a => a.worker_uuid && !workerUUIDs.includes(a.worker_uuid));
  void orphanedAccounts; // reserved for future alerting

  // Prefetch worker schedule URL when activeWorkerId changes.
  // Keyed per worker: switching W1→W2→W1 reuses W1's cached tunnel URL.
  useEffect(() => {
    if (!activeWorkerId || !isAuthenticated) return;
    // Skip if we already have a valid cache for this specific worker (50 min TTL)
    const existing = prefetchedScheduleCache.get(activeWorkerId);
    if (existing && Date.now() - existing.timestamp < 3000000) return;

    (async () => {
      try {
        console.log('🔗 [Prefetch] Fetching worker schedule URL for', activeWorkerId);
        const urlResponse = await apiService.getWorkerScheduleURL(activeWorkerId);
        const { worker, jwt_token } = urlResponse;
        const entry = {
          workerURL: worker.url,
          jwt: jwt_token || '',
          timestamp: Date.now(),
          workerId: activeWorkerId,
        };
        setPrefetchedScheduleCache(prev => {
          const next = new Map(prev);
          next.set(activeWorkerId, entry);
          return next;
        });
        console.log('✅ [Prefetch] Worker schedule URL cached for', activeWorkerId);
      } catch (err) {
        console.warn('⚠️ [Prefetch] Failed to prefetch worker schedule URL:', err);
      }
    })();
  }, [activeWorkerId, isAuthenticated]); // prefetchedScheduleCache intentionally omitted — stable Map ref

  // Build workerStatusMap: { [worker_uuid]: 'online' | 'idle' | 'offline' | 'expired' }
  // Passed to AccountManagement → AccountList so the badge is derived from
  // worker runtime state (Redis) instead of the stale seeding_accounts.status.
  // 'idle'    = scheduled worker not currently running (normal between executions)
  // 'offline' = realtime worker not responding (potential problem)
  const workerStatusMap = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    workers.forEach(w => {
      if (w.status === 'expired') {
        map[w.uuid] = 'expired';
      } else if (w.isOnline) {
        map[w.uuid] = 'online';
      } else if ((w as any).workerMode === 'scheduled') {
        map[w.uuid] = 'idle'; // scheduled worker between runs — not an error
      } else {
        map[w.uuid] = 'offline';
      }
    });
    return map;
  }, [workers]);

  useEffect(() => {
    // ── DEV BYPASS ─────────────────────────────────────────────────────────────
    // Chỉ chạy trong `vite dev` (import.meta.env.DEV === true).
    // Vite/Rollup loại bỏ toàn bộ khối này khi build production — zero footprint.
    //
    // Cách dùng:
    //   1. cp .env.example .env.local
    //   2. Thêm: VITE_DEV_TOKEN=<JWT lấy từ biztada.com DevTools → Application → localStorage>
    //   3. npm run dev → vào thẳng giao diện, không cần login
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_TOKEN) {
      authService.setAuthToken(
        import.meta.env.VITE_DEV_TOKEN as string,
        import.meta.env.VITE_DEV_BUSINESS_ID as string | undefined,
      );
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }
    // ───────────────────────────────────────────────────────────────────────────

    const isInIframe = window !== window.parent;

    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedShellOrigin(event.origin)) {
        // Log unexpected origins to help diagnose origin mismatches
        if (event.data?.type === 'BIZTADA_AUTH_TOKEN') {
          console.warn('[Auth] BLOCKED — BIZTADA_AUTH_TOKEN from unexpected origin:', event.origin);
        }
        return;
      }
      if (event.data?.type === 'BIZTADA_AUTH_TOKEN' && event.data?.token) {
        authService.setAuthToken(event.data.token, event.data.businessId);
        setIsAuthenticated(true);
        setIsLoading(false);
      }
      if (event.data?.type === 'BIZTADA_LANGUAGE_CHANGE' && event.data?.locale) {
        i18n.changeLanguage(event.data.locale);
      }
      if (event.data?.type === 'BIZTADA_CLEAR_STORAGE') {
        localStorage.clear();
        sessionStorage.clear();
      }
    };

    if (isInIframe) {
      // ── Fast resume: use cached JWT immediately while waiting for fresh token ──
      // This makes F5 / Ctrl+Shift+R show content instantly instead of a blank
      // screen. The shell will push a fresh token shortly after via postMessage.
      if (authService.loadCachedIframeToken()) {
        setIsAuthenticated(true);
        setIsLoading(false);
        // Fall through — still register listener so shell can refresh the token
      }

      // Track whether the shell has sent a fresh token this session
      let shellTokenReceived = false;

      const handleMessageWrapped = (event: MessageEvent) => {
        handleMessage(event);
        if (event.data?.type === 'BIZTADA_AUTH_TOKEN' && event.data?.token) {
          shellTokenReceived = true;
        }
      };

      window.addEventListener('message', handleMessageWrapped);

      // ── Retry IFRAME_READY every 1 s until the shell sends BIZTADA_AUTH_TOKEN ──
      // The shell's JWT fetch (authorize → token exchange) takes ~200-2000 ms.
      // Sending IFRAME_READY only once creates a race where the shell receives it
      // before the JWT is ready and trySend() fails. Retrying for up to 30 s
      // guarantees the shell can always deliver the token.
      const sendReady = () =>
        window.parent.postMessage({ type: 'IFRAME_READY', app: 'marketing' }, '*');
      sendReady(); // immediate first signal
      let retries = 0;
      const retryInterval = setInterval(() => {
        if (shellTokenReceived) { clearInterval(retryInterval); return; }
        retries++;
        if (retries <= 30) sendReady();
        else clearInterval(retryInterval); // give up after 30 s
      }, 1000);

      // Fallback: if no token in 10s AND no cached token, stop loading with error
      const timeout = setTimeout(() => {
        if (!authService.isAuthenticated()) {
          console.warn('[Auth] No token received from shell in 10s');
          setIsLoading(false);
        }
      }, 10000);

      return () => {
        window.removeEventListener('message', handleMessageWrapped);
        clearInterval(retryInterval);
        clearTimeout(timeout);
      };
    }

    // Telegram Mini App (opened directly, not via shell iframe)
    const initMiniApp = async () => {
      try {
        if (window.Telegram?.WebApp?.initData) {
          const success = await authService.initMiniApp();
          setIsAuthenticated(success);
          if (!success) {
            window.Telegram?.WebApp?.showAlert('Authentication failed. Please restart the app.');
          }
        } else {
          // Not in iframe and not a Mini App — nothing to do
          console.warn('[Auth] Not in iframe and no Telegram initData — unauthenticated');
        }
      } catch (error) {
        console.error('[Auth] Mini app init failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initMiniApp();
  }, []);

  // Fetch real data after authentication
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchData();
  }, [isAuthenticated]);

  // Reusable data fetch function (can be called from child components)
  const fetchData = async () => {
    setIsDataLoading(true);
    try {
      // Fetch workers and accounts in parallel
      const [workersRes, accountsRes] = await Promise.all([
        apiService.getWorkers(),
        apiService.getAccounts(),
      ]);

      // Parse workers - backend returns { success: true, data: { workers: [...] } }
      const workersArray = Array.isArray(workersRes) ? workersRes : 
                          (workersRes?.data?.workers || workersRes?.workers || workersRes?.data || []);
      
      // Parse accounts - backend returns {success, data: {accounts: []}}
      let accountsArray = [];
      if (Array.isArray(accountsRes)) {
        accountsArray = accountsRes;
      } else if (accountsRes.data?.accounts) {
        accountsArray = accountsRes.data.accounts;
      } else if (accountsRes.accounts) {
        accountsArray = accountsRes.accounts;
      }
      
      // Ensure accountsArray is always an array
      if (!Array.isArray(accountsArray)) {
        accountsArray = [];
      }
      
      setAccounts(accountsArray);
      
      // Calculate account count per worker
      const transformedWorkers: Worker[] = workersArray.map((w: any) => {
        const workerAccounts = accountsArray.filter((a: any) => a.worker_uuid === w.worker_uuid);
        
        return {
          uuid: w.worker_uuid || w.id || w.uuid,
          workerName: w.worker_name || w.worker_uuid || `Worker ${w.id || w.uuid}`,
          workerMode: w.worker_mode || 'scheduled',
          isOnline: w.worker_status === 'online' || w.status === 'online',
          status: (w.worker_status === 'expired' || w.status === 'expired') ? 'expired' : 
                  (w.worker_status === 'offline' || w.status === 'offline') ? 'inactive' : 'active',
          expiresAt: w.expires_at || w.expiresAt,
          accountCount: workerAccounts.length,
        };
      });

      setWorkers(transformedWorkers);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setWorkers([]);
      setAccounts([]);
    } finally {
      setIsDataLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <LoadingSpinner size="md" text="Loading..." />
      </div>
    );
  }

  // Allow OAuth callback to complete in standalone browser/webview where shell JWT
  // is not available. The callback component uses nonce/session endpoints.
  if (!isAuthenticated && shouldHandleFanpageCallback) {
    return (
      <Router basename="/">
        <Routes>
          <Route path="/fanpage-callback" element={<FanpageOAuthCallback />} />
          <Route path="*" element={<FanpageOAuthCallback />} />
        </Routes>
      </Router>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <div className="text-center text-gray-500">
          <p>Vui lòng đăng nhập qua biztada.com</p>
        </div>
      </div>
    );
  }

  return (
    <Router basename="/">
      <IframeNavSync />
      <WorkflowTourProvider>
      <div className="app-container">
        <PlatformSidebar
          activePlatform={activePlatform}
          onPlatformSelect={setActivePlatform}
          platformCounts={platformCounts}
        />
        
        <WorkerListSidebar
          workers={filteredWorkers}
          activeWorkerId={activeWorkerId}
          onWorkerSelect={setActiveWorkerId}
        />
        
        <div className="app-main">
          <AppHeader />
          <main className="app-content">
            {isDataLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner size="md" text="Loading data..." />
              </div>
            ) : (
              <Routes>
                <Route path="/" element={<Dashboard activeWorkerId={activeWorkerId} />} />
                <Route path="/accounts" element={<AccountManagement activePlatform={activePlatform} activeWorkerId={activeWorkerId} onDataChange={fetchData} workerStatusMap={workerStatusMap} />} />
                <Route path="/campaigns" element={<CampaignManagement />} />
                <Route path="/workflows/:id?" element={<WorkflowBuilder />} />
                <Route path="/group-post-leads" element={<GroupPostLeadsDashboard />} />
                <Route path="/engagement-post-leads" element={<EngagementPostLeadsDashboard />} />
                <Route path="/joined-groups" element={<JoinedGroupsDashboard />} />
                <Route path="/seeding-contents" element={<SeedingContentsManagement />} />
                <Route path="/schedules" element={<ScheduleDashboard activeWorkerId={activeWorkerId} prefetchedScheduleCache={activeWorkerId ? (prefetchedScheduleCache.get(activeWorkerId) ?? null) : null} />} />
                <Route path="/reports" element={<ReportDashboard activeWorkerId={activeWorkerId} />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/fanpage-callback" element={<FanpageOAuthCallback />} />
              </Routes>
            )}
          </main>
        </div>
      </div>
      <AgentMount />
      {/* TikTok QR Modal — shown when SSE pushes a tiktok_qr_ready event */}
      {tiktokQRAccount && (
        <TikTokQRModal
          account={tiktokQRAccount}
          initialQR={tiktokQRInitial}
          onClose={() => { setTiktokQRAccount(null); setTiktokQRInitial(undefined); }}
        />
      )}
      </WorkflowTourProvider>
    </Router>
  );
}

export default App;
